


import React, { useState, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { StatCard } from './components/StatCard';
import { AiAnalysisCard } from './components/AiAnalysisCard';
import { AiAssistant } from './components/AiAssistant';
import { ServiceCard } from './components/ServiceCard';
import { ServiceEditModal } from './components/ServiceEditModal';
import { ProfileEditModal } from './components/ProfileEditModal';
import { PlatformConnectModal } from './components/PlatformConnectModal';
import { ReviewModal } from './components/ReviewModal';
import { ProjectApplicationModal } from './components/ProjectApplicationModal';
import { CreateCampaignView } from './components/CreateCampaignView';
import { OrderMonitoring } from './components/OrderMonitoring';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { SettingsView } from './components/SettingsView';
import { NotificationDropdown } from './components/NotificationDropdown';
import { ChatView } from './components/ChatView';
import { LanguageDropdown } from './components/LanguageDropdown';
import { AdminDashboard } from './components/AdminDashboard';
import { SbtBadgeList } from './components/SbtBadgeList';
import { CampaignCalendar } from './components/CampaignCalendar';
import { KolGrabMode } from './components/KolGrabMode';
import { PointsCard } from './components/PointsCard'; 
import { KolFundraisingCard } from './components/KolFundraisingCard';
import { KolLevelCard } from './components/KolLevelCard';
import { LandingView } from './components/LandingView'; 
import { ReferralView } from './components/ReferralView';
import { DisputeResolutionView } from './components/DisputeResolutionView'; 
import { SquadMarketplace } from './components/SquadMarketplace'; 
import { ServiceBookingModal } from './components/ServiceBookingModal'; // Added
import { ViewState, KolCategory, KolProfile, OrderStatus, Language, UserRole, SocialPlatform, ServicePackage, Order, SocialAccount, ProjectProfile, Notification, ChatConversation, CampaignApplication, KolSquad } from './types';
import { MOCK_KOLS, MOCK_ORDERS, MOCK_PROJECTS, MOCK_NOTIFICATIONS, MOCK_CHATS, MOCK_DISPUTES, MOCK_SQUADS } from './constants';
import { TRANSLATIONS, getStatusLabel, getCategoryLabel } from './translations';
import { 
  Search, CheckCircle, Clock, 
  Wallet, Users, ChevronLeft, 
  Bot, Menu, SortAsc, Plus, Edit3, MessageSquare, Briefcase, Layers,
  Twitter, Youtube, Instagram, Facebook, Tv, Music, BookHeart, Globe
} from 'lucide-react';

const App: React.FC = () => {
  // --- Global State ---
  const [hasEnteredApp, setHasEnteredApp] = useState(false); 
  const [currentView, setCurrentView] = useState<ViewState>(ViewState.DASHBOARD);
  const [userRole, setUserRole] = useState<UserRole>(UserRole.PROJECT);
  const [language, setLanguage] = useState<Language>('en');
  const t = TRANSLATIONS[language];

  // --- UI State ---
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [aiChatContext, setAiChatContext] = useState<string | undefined>(undefined);
  const [grabModeEnabled, setGrabModeEnabled] = useState(false); 

  // --- Data State ---
  const [allKols, setAllKols] = useState<KolProfile[]>(MOCK_KOLS);
  const [orders, setOrders] = useState<Order[]>(MOCK_ORDERS);
  const [projects, setProjects] = useState<ProjectProfile[]>(MOCK_PROJECTS);
  const [notifications, setNotifications] = useState<Notification[]>(MOCK_NOTIFICATIONS);
  const [chats, setChats] = useState<ChatConversation[]>(MOCK_CHATS);
  const [squads, setSquads] = useState<KolSquad[]>(MOCK_SQUADS);

  // --- Selections ---
  const [selectedKol, setSelectedKol] = useState<KolProfile | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedCampaignForManagement, setSelectedCampaignForManagement] = useState<ProjectProfile | null>(null);
  
  // --- Filter/Search State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<KolCategory>(KolCategory.ALL);
  const [sortOption, setSortOption] = useState<string>('recommended');
  const [searchProjectQuery, setSearchProjectQuery] = useState('');
  const [searchMyCampaignsQuery, setSearchMyCampaignsQuery] = useState('');

  // --- Modal States ---
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPlatformModalOpen, setIsPlatformModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false); // New for Booking
  
  // --- Modal Data Context ---
  const [myKolProfile, setMyKolProfile] = useState<KolProfile>(MOCK_KOLS[0]); 
  const [myProjectProfile, setMyProjectProfile] = useState<ProjectProfile>(MOCK_PROJECTS[0]); 
  const [editingService, setEditingService] = useState<ServicePackage | null>(null);
  const [reviewingOrder, setReviewingOrder] = useState<Order | null>(null);
  const [applyingProject, setApplyingProject] = useState<ProjectProfile | null>(null);
  const [bookingData, setBookingData] = useState<{pkg: ServicePackage, kol: KolProfile} | null>(null); // New for Booking

  // --- Handlers ---

  const handleEnterApp = (role: UserRole) => {
    setUserRole(role);
    setHasEnteredApp(true);
    if (role === UserRole.ADMIN) setCurrentView(ViewState.ADMIN_DASHBOARD);
    else setCurrentView(ViewState.DASHBOARD);
  };

  const handleToggleRole = () => {
    let newRole: UserRole;
    if (userRole === UserRole.PROJECT) newRole = UserRole.KOL;
    else if (userRole === UserRole.KOL) newRole = UserRole.ADMIN;
    else newRole = UserRole.PROJECT;

    setUserRole(newRole);
    if (newRole === UserRole.ADMIN) setCurrentView(ViewState.ADMIN_DASHBOARD);
    else setCurrentView(ViewState.DASHBOARD);
    setIsSidebarOpen(false);
  };

  const handleChatWithUser = (targetUserId: string, targetName: string, targetAvatar: string, targetRole: UserRole) => {
    const existingChat = chats.find(c => c.partnerId === targetUserId);
    if (existingChat) {
        // Chat exists
    } else {
      const newChat: ChatConversation = {
        id: `chat_${Date.now()}`,
        partnerId: targetUserId,
        partnerName: targetName,
        partnerAvatar: targetAvatar,
        partnerRole: targetRole,
        lastMessage: t.online,
        lastMessageTime: 'Now',
        unreadCount: 0,
        messages: []
      };
      setChats(prev => [newChat, ...prev]);
    }
    setCurrentView(ViewState.CHAT);
  };

  const handlePostCampaign = (campaign: ProjectProfile) => {
    setProjects(prev => [campaign, ...prev]);
    setMyProjectProfile(prev => ({ ...prev, points: prev.points + 500 }));
    setNotifications(prev => [{
      id: `pts-${Date.now()}`,
      title: t.pointsEarned,
      message: `+500 ${t.linkKolPoints}`,
      type: 'success',
      date: 'Just now',
      read: false
    }, ...prev]);
    setCurrentView(ViewState.MY_CAMPAIGNS);
  };

  const handleBookService = (pkg: ServicePackage, kol: KolProfile) => {
    // Open the Booking Modal instead of creating instantly
    setBookingData({ pkg, kol });
    setIsBookingModalOpen(true);
  };

  const handleConfirmBooking = () => {
    if (!bookingData) return;
    const { pkg, kol } = bookingData;

    const newOrder: Order = {
      id: `ORD-${Date.now()}`,
      kolId: kol.id,
      kolName: kol.name,
      kolAvatar: kol.avatar,
      projectName: 'SuperNova Protocol',
      amount: pkg.price,
      currency: 'USDT',
      status: OrderStatus.PENDING_ACCEPTANCE, // Start as Pending Acceptance after staking
      dateCreated: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + pkg.deliveryDays * 86400000).toISOString().split('T')[0],
      serviceTitle: pkg.title
    };

    setOrders([newOrder, ...orders]);
    setMyProjectProfile(prev => ({ ...prev, points: prev.points + 100 }));
    
    setIsBookingModalOpen(false);
    setBookingData(null);
    setCurrentView(ViewState.ORDERS);
    
    setNotifications(prev => [{
      id: `n-${Date.now()}`,
      title: t.bookingSent,
      message: `${t.bookingSentDesc} - ${kol.name}`,
      type: 'success',
      date: 'Just now',
      read: false
    }, ...prev]);

    // Simulate Notification for KOL side (if we were logging in as KOL)
    // In real app, backend handles this
  };

  const handleAcceptOrder = (orderId: string) => {
      const updatedOrders = orders.map(o => o.id === orderId ? { ...o, status: OrderStatus.IN_PROGRESS } : o);
      setOrders(updatedOrders);
      alert("Order Accepted! Funds are now locked in Escrow.");
  };

  const handleDeclineOrder = (orderId: string) => {
      // In real app, this would trigger refund logic
      const updatedOrders = orders.filter(o => o.id !== orderId); // Removing for demo or set to CANCELLED
      setOrders(updatedOrders);
      alert("Order Declined. Funds refunded to Project.");
  };

  const handleBookSquad = (squad: KolSquad, requirements: string, date: string) => {
    const discountedPrice = squad.totalPrice * (1 - squad.discountPercentage / 100);
    const newOrder: Order = {
        id: `ORD-SQD-${Date.now()}`,
        kolId: squad.leaderId, // Linked to leader for payment/management
        kolName: `[Squad] ${squad.name}`, // Distinct name
        kolAvatar: squad.coverImage,
        projectName: 'SuperNova Protocol', // Hardcoded for demo
        amount: discountedPrice,
        currency: 'USDT',
        status: OrderStatus.NEGOTIATING,
        dateCreated: new Date().toISOString().split('T')[0],
        dueDate: date,
        serviceTitle: 'Squad Campaign Bundle',
        // In a real app, requirements would be stored in a description field
    };
    
    setOrders([newOrder, ...orders]);
    setCurrentView(ViewState.ORDERS);
    setNotifications(prev => [{
      id: `n-${Date.now()}`,
      title: t.bookingSuccess,
      message: `You booked ${squad.name}`,
      type: 'success',
      date: 'Just now',
      read: false
    }, ...prev]);
  };

  const handleApplyToProject = (project: ProjectProfile) => {
    setApplyingProject(project);
    setIsApplicationModalOpen(true);
  };

  const submitApplication = (message: string, price: number) => {
    setIsApplicationModalOpen(false);
    alert(t.applicationSent);
  };

  const handleSaveService = (service: ServicePackage) => {
    setMyKolProfile(prev => {
      const existingIdx = prev.packages.findIndex(p => p.id === service.id);
      let newPackages = [...prev.packages];
      if (existingIdx >= 0) {
        newPackages[existingIdx] = service;
      } else {
        newPackages.push(service);
      }
      return { ...prev, packages: newPackages };
    });
    setIsServiceModalOpen(false);
  };

  const handleDeleteService = (service: ServicePackage) => {
    setMyKolProfile(prev => ({
      ...prev,
      packages: prev.packages.filter(p => p.id !== service.id)
    }));
    setIsServiceModalOpen(false);
  };

  const handleSaveProfile = (data: Partial<KolProfile>) => {
    setMyKolProfile(prev => ({ ...prev, ...data }));
    setIsProfileModalOpen(false);
  };

  const handleConnectPlatform = (account: SocialAccount) => {
    setMyKolProfile(prev => ({
      ...prev,
      socialAccounts: [...prev.socialAccounts, account]
    }));
  };

  const handleViewKol = (kol: KolProfile) => {
    setSelectedKol(kol);
    setCurrentView(ViewState.KOL_PROFILE);
  };

  const handleAiFilter = (query: string) => {
      setSearchQuery(query);
      if (query.includes('meme')) setSelectedCategory(KolCategory.MEME);
      if (query.includes('defi')) setSelectedCategory(KolCategory.DEFI);
      if (query.includes('nft')) setSelectedCategory(KolCategory.NFT);
      if (query.includes('game')) setSelectedCategory(KolCategory.GAMEFI);
  };

  const handleAcceptApplicant = (campaignId: string, applicant: CampaignApplication) => {
      const newOrder: Order = {
          id: `ORD-${Date.now()}`,
          kolId: applicant.kolId,
          kolName: applicant.kolName,
          kolAvatar: applicant.kolAvatar,
          projectName: 'SuperNova Protocol',
          amount: applicant.proposedPrice,
          currency: 'USDT',
          status: OrderStatus.IN_PROGRESS, 
          dateCreated: new Date().toISOString().split('T')[0],
          dueDate: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
          serviceTitle: 'Campaign Application'
      };
      setOrders(prev => [newOrder, ...prev]);
      setMyProjectProfile(prev => ({ ...prev, points: prev.points + 200 }));
      alert(`Accepted ${applicant.kolName}. ${t.telegramAlertSent} ${applicant.kolHandle}`);
      setSelectedCampaignForManagement(null);
      setCurrentView(ViewState.ORDERS);
  };

  // --- Order Lifecycle Handlers ---
  const handleSubmitWork = (orderId: string) => {
      const updatedOrders = orders.map(o => o.id === orderId ? {
          ...o,
          status: OrderStatus.SUBMITTED,
          submissionTime: new Date().toISOString()
      } : o);
      setOrders(updatedOrders);
      // Update selected order in view if open
      const updatedOrder = updatedOrders.find(o => o.id === orderId);
      if (updatedOrder) setSelectedOrder(updatedOrder);
  };

  const handleApproveOrder = (orderId: string) => {
      const updatedOrders = orders.map(o => o.id === orderId ? {
          ...o,
          status: OrderStatus.COMPLETED
      } : o);
      setOrders(updatedOrders);
      // Update selected order in view
      const updatedOrder = updatedOrders.find(o => o.id === orderId);
      if (updatedOrder) setSelectedOrder(updatedOrder);
      
      alert("Funds Released Successfully!");
  };

  const handleRaiseDispute = (orderId: string) => {
      const updatedOrders = orders.map(o => o.id === orderId ? {
          ...o,
          status: OrderStatus.DISPUTED
      } : o);
      setOrders(updatedOrders);
       // Update selected order in view
      const updatedOrder = updatedOrders.find(o => o.id === orderId);
      if (updatedOrder) setSelectedOrder(updatedOrder);

      alert("Dispute raised. Admin will review the case.");
  };

  const getPlatformIcon = (platform: SocialPlatform, size: number = 14) => {
    switch (platform) {
      case SocialPlatform.TWITTER: return <Twitter size={size} className="text-sky-400" />;
      case SocialPlatform.YOUTUBE: return <Youtube size={size} className="text-red-500" />;
      case SocialPlatform.INSTAGRAM: return <Instagram size={size} className="text-pink-500" />;
      case SocialPlatform.FACEBOOK: return <Facebook size={size} className="text-blue-600" />;
      case SocialPlatform.TIKTOK: return <Music size={size} className="text-white" />;
      case SocialPlatform.BILIBILI: return <Tv size={size} className="text-blue-300" />;
      case SocialPlatform.RED_BOOK: return <BookHeart size={size} className="text-red-400" />;
      default: return <Globe size={size} className="text-slate-400" />;
    }
  };

  // --- Render Functions ---

  const renderDashboard = () => {
    if (userRole === UserRole.ADMIN) return <AdminDashboard language={language} orders={orders} usersCount={allKols.length + projects.length} />;

    if (userRole === UserRole.KOL) {
      const myOrders = orders.filter(o => o.kolId === myKolProfile.id);
      const pendingOrders = myOrders.filter(o => o.status === OrderStatus.PENDING_ACCEPTANCE);

      return (
        <div className="space-y-6 animate-in fade-in duration-500">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-white">{t.kolOverview}</h1>
            <span className="bg-rose-500/20 text-rose-400 text-xs px-2 py-1 rounded-full border border-rose-500/30">Beta</span>
          </div>
          <p className="text-slate-400 text-sm -mt-4">{t.welcomeKol}</p>
          
          <KolLevelCard points={myKolProfile.points} language={language} />
          <KolFundraisingCard language={language} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <PointsCard points={myKolProfile.points} language={language} />
            <StatCard label={t.totalEarnings} value="$45,250" trend="+15%" trendUp={true} icon={Wallet} colorClass="text-emerald-400" onClick={() => setCurrentView(ViewState.ANALYTICS)} />
            <StatCard label={t.pendingOrders} value={pendingOrders.length.toString()} trend="Action Req." trendUp={false} icon={Clock} colorClass="text-amber-400" onClick={() => setCurrentView(ViewState.ORDERS)} />
            <StatCard label={t.profileVisits} value="12.5k" trend="+12%" trendUp={true} icon={Users} colorClass="text-blue-400" onClick={() => setCurrentView(ViewState.ANALYTICS)} />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                  <CampaignCalendar orders={myOrders} language={language} userRole={UserRole.KOL} />
              </div>

              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <h3 className="font-bold text-white mb-4">{t.recentRequests}</h3>
                <div className="space-y-3">
                  {/* Dynamic Pending Orders */}
                  {pendingOrders.length > 0 ? (
                      pendingOrders.map(order => (
                          <div key={order.id} className="flex items-center justify-between bg-slate-900/50 p-4 rounded-lg border border-slate-700 animate-in fade-in slide-in-from-right-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                                    {order.projectName[0]}
                                </div>
                                <div>
                                    <p className="font-bold text-white text-sm">{order.projectName}</p>
                                    <p className="text-xs text-slate-400">{order.serviceTitle} • ${order.amount}</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => handleDeclineOrder(order.id)} className="px-3 py-1.5 bg-slate-700 hover:bg-rose-900 text-white text-xs rounded-lg transition-colors">{t.decline}</button>
                                <button onClick={() => handleAcceptOrder(order.id)} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg transition-colors font-bold">{t.accept}</button>
                            </div>
                        </div>
                      ))
                  ) : (
                      // Fallback visual if no real pending orders
                      <div className="flex items-center justify-between bg-slate-900/50 p-4 rounded-lg border border-slate-700 opacity-60">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center text-white font-bold">P</div>
                            <div>
                                <p className="font-bold text-white text-sm">Pixel Wars</p>
                                <p className="text-xs text-slate-400">Sent an offer for "GameFi Beta Launch"</p>
                            </div>
                        </div>
                        <span className="text-xs text-slate-500">Demo (Processed)</span>
                      </div>
                  )}
                </div>
              </div>
          </div>
        </div>
      );
    }

    // Project Dashboard
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <h1 className="text-2xl font-bold text-white">{t.overview}</h1>
        <p className="text-slate-400 text-sm -mt-4">{t.welcomeBack}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <PointsCard points={myProjectProfile.points} language={language} />
          <StatCard label={t.totalSpent} value="$12,450" trend="+12%" trendUp={true} icon={Wallet} colorClass="text-emerald-400" onClick={() => setCurrentView(ViewState.ANALYTICS)} />
          <StatCard label={t.activeContracts} value="3" trend="On Track" trendUp={true} icon={Clock} colorClass="text-blue-400" onClick={() => setCurrentView(ViewState.ORDERS)} />
          <StatCard label={t.avgRoi} value="2.8x" trend="+5%" trendUp={true} icon={Users} colorClass="text-orange-400" onClick={() => setCurrentView(ViewState.ANALYTICS)} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
           <div className="space-y-6">
               <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                  <div className="flex justify-between items-center mb-6">
                     <h3 className="font-bold text-white">{t.activeContracts}</h3>
                     <button onClick={() => setCurrentView(ViewState.ORDERS)} className="text-indigo-400 text-sm hover:text-indigo-300">{t.viewAllContracts}</button>
                  </div>
                  <div className="space-y-3">
                     {orders.slice(0, 3).map(order => (
                        <div key={order.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-indigo-500/30 transition-colors cursor-pointer" onClick={() => { setSelectedOrder(order); setCurrentView(ViewState.ORDER_DETAIL); }}>
                           <div className="flex items-center gap-3">
                              <img src={order.kolAvatar} alt="" className="w-8 h-8 rounded-full" />
                              <div>
                                 <p className="text-sm font-bold text-white">{order.kolName}</p>
                                 <p className="text-xs text-slate-400">{order.serviceTitle}</p>
                              </div>
                           </div>
                           <span className={`px-2 py-1 rounded text-xs font-bold ${
                              order.status === OrderStatus.COMPLETED ? 'bg-emerald-500/10 text-emerald-400' :
                              order.status === OrderStatus.IN_PROGRESS ? 'bg-indigo-500/10 text-indigo-400' :
                              order.status === OrderStatus.PENDING_ACCEPTANCE ? 'bg-amber-500/10 text-amber-400' :
                              'bg-slate-700 text-slate-400'
                           }`}>
                              {getStatusLabel(order.status, language)}
                           </span>
                        </div>
                     ))}
                  </div>
               </div>
               
               <CampaignCalendar orders={orders} language={language} />
           </div>
           
           <div className="space-y-6">
               <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 flex flex-col items-center justify-center text-center">
                  <div className="bg-slate-900/50 p-4 rounded-full mb-4">
                     <Bot size={32} className="text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{t.aiAnalysis}</h3>
                  <p className="text-slate-400 text-sm mb-4 max-w-xs">Use our AI agent to find the perfect KOLs for your next campaign.</p>
                  <button onClick={() => { setAiChatContext(undefined); setIsAiChatOpen(true); }} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors">
                     {t.askAi}
                  </button>
               </div>
           </div>
        </div>
      </div>
    );
  };

  const renderMarketplace = () => {
    let filtered = allKols.filter(k => 
      (selectedCategory === KolCategory.ALL || k.categories.includes(selectedCategory)) &&
      (k.name.toLowerCase().includes(searchQuery.toLowerCase()) || k.handle.toLowerCase().includes(searchQuery.toLowerCase()) || k.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
    );

    if (sortOption === 'trust_desc') filtered.sort((a, b) => b.trustScore - a.trustScore);
    else if (sortOption === 'followers_desc') filtered.sort((a, b) => b.totalFollowers - a.totalFollowers);
    else if (sortOption === 'price_asc') filtered.sort((a, b) => a.priceRange.min - b.priceRange.min);

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{t.findTalent}</h1>
            <p className="text-slate-400 text-sm">{t.findTalentSubtitle}</p>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
             <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-2.5 text-slate-500 w-4 h-4" />
                <input 
                  type="text" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t.searchPlaceholder}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
             </div>
             <button 
               onClick={() => { setAiChatContext(undefined); setIsAiChatOpen(true); }}
               className="bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-lg transition-colors"
               title={t.askAi}
             >
                <Bot size={20} />
             </button>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {Object.values(KolCategory).map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
              }`}
            >
              {getCategoryLabel(cat, language)}
            </button>
          ))}
        </div>

        <div className="flex justify-end">
             <div className="flex items-center gap-2">
                 <SortAsc size={14} className="text-slate-500" />
                 <select 
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="bg-transparent text-sm text-slate-400 border-none focus:ring-0 cursor-pointer"
                 >
                    <option value="recommended">{t.sortRecommended}</option>
                    <option value="trust_desc">{t.sortTrustHigh}</option>
                    <option value="followers_desc">{t.sortFollowersHigh}</option>
                    <option value="price_asc">{t.sortPriceLow}</option>
                 </select>
             </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map(kol => (
            <div key={kol.id} className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden hover:border-indigo-500/50 transition-all group">
              <div className="p-5">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex gap-3">
                    <img src={kol.avatar} alt={kol.name} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <h3 className="font-bold text-white group-hover:text-indigo-400 transition-colors flex items-center gap-1">
                          {kol.name}
                          {kol.trustScore > 90 && <CheckCircle size={14} className="text-emerald-500" />}
                      </h3>
                      <p className="text-xs text-slate-400">{kol.handle}</p>
                    </div>
                  </div>
                  <div className="bg-slate-900 px-2 py-1 rounded text-xs font-bold text-indigo-400 border border-slate-700">
                    {kol.trustScore} {t.trust}
                  </div>
                </div>
                
                <p className="text-xs text-slate-400 line-clamp-2 mb-4 h-8">{kol.bio}</p>

                <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
                    {kol.socialAccounts.map((acc, idx) => (
                        <div key={idx} className="bg-slate-900/80 p-1.5 rounded-lg border border-slate-700 flex items-center gap-1" title={`${acc.platform}: ${acc.followers} followers`}>
                            {getPlatformIcon(acc.platform, 12)}
                            {acc.verified && <CheckCircle size={8} className="text-emerald-500" />}
                        </div>
                    ))}
                </div>

                <div className="flex flex-wrap gap-1 mb-4 h-6 overflow-hidden">
                    {kol.tags.slice(0,3).map(tag => (
                        <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-slate-700/50 text-slate-300 rounded border border-slate-600">
                            {tag}
                        </span>
                    ))}
                </div>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="text-center p-2 bg-slate-900/50 rounded-lg">
                    <p className="text-[10px] text-slate-500 uppercase">{t.followers}</p>
                    <p className="font-bold text-white text-xs">{(kol.totalFollowers / 1000).toFixed(1)}k</p>
                  </div>
                  <div className="text-center p-2 bg-slate-900/50 rounded-lg">
                    <p className="text-[10px] text-slate-500 uppercase">{t.engScore}</p>
                    <p className="font-bold text-emerald-400 text-xs">{kol.engagementScore}</p>
                  </div>
                  <div className="text-center p-2 bg-slate-900/50 rounded-lg">
                    <p className="text-[10px] text-slate-500 uppercase">{t.estPrice}</p>
                    <p className="font-bold text-white text-xs">${kol.priceRange.min}</p>
                  </div>
                </div>

                <button 
                  onClick={() => handleViewKol(kol)}
                  className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {t.viewProfile}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderKolProfile = () => {
    const profile = userRole === UserRole.KOL && !selectedKol ? myKolProfile : selectedKol;
    if (!profile) return null;
    const isOwner = userRole === UserRole.KOL && !selectedKol;

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <button 
          onClick={() => setCurrentView(ViewState.MARKETPLACE)} 
          className={`flex items-center gap-2 text-slate-400 hover:text-white mb-4 ${isOwner ? 'hidden' : ''}`}
        >
          <ChevronLeft size={16} /> {t.backToMarketplace}
        </button>

        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="h-32 bg-gradient-to-r from-indigo-900 to-slate-900"></div>
          <div className="px-8 pb-8">
            <div className="relative -mt-12 mb-6 flex justify-between items-end">
              <div className="flex items-end gap-6">
                <img src={profile.avatar} alt={profile.name} className="w-24 h-24 rounded-full border-4 border-slate-800 object-cover" />
                <div className="mb-1">
                  <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                    {profile.name}
                    {profile.trustScore > 90 && <CheckCircle size={20} className="text-emerald-500" />}
                  </h1>
                  <p className="text-slate-400">{profile.handle}</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                 {isOwner ? (
                   <>
                      <button onClick={() => setIsProfileModalOpen(true)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2">
                        <Edit3 size={16} /> {t.editProfile}
                      </button>
                   </>
                 ) : (
                    <button onClick={() => handleChatWithUser(profile.id, profile.name, profile.avatar, UserRole.KOL)} className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-600 transition-colors">
                        {t.sendMessage}
                    </button>
                 )}
              </div>
            </div>

            <p className="text-slate-300 max-w-3xl mb-6">{profile.bio}</p>

            <div className="flex flex-wrap items-center gap-6 mb-8">
                <div className="flex items-center gap-3">
                    {profile.socialAccounts.map((acc, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-slate-900/50 px-3 py-1.5 rounded-lg border border-slate-700 hover:border-indigo-500/50 transition-colors cursor-pointer" title="View Profile">
                             {getPlatformIcon(acc.platform, 16)}
                             <span className="font-bold text-white text-sm">{(acc.followers / 1000).toFixed(1)}k</span>
                             {acc.verified && <CheckCircle size={14} className="text-emerald-500" />}
                        </div>
                    ))}
                    {isOwner && (
                        <button onClick={() => setIsPlatformModalOpen(true)} className="flex items-center gap-1 text-xs text-indigo-400 hover:text-white px-3 py-1.5 border border-dashed border-indigo-500/30 rounded-lg hover:bg-indigo-500/10 transition-colors">
                            <Plus size={14} /> {t.addPlatform}
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
                 <p className="text-slate-500 text-xs uppercase mb-1">{t.avgViews}</p>
                 <h4 className="text-xl font-bold text-white">{(profile.avgViews / 1000).toFixed(1)}k</h4>
              </div>
              <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
                 <p className="text-slate-500 text-xs uppercase mb-1">{t.engagement}</p>
                 <h4 className="text-xl font-bold text-emerald-400">{profile.engagementScore}%</h4>
              </div>
              <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
                 <p className="text-slate-500 text-xs uppercase mb-1">{t.trustScore}</p>
                 <h4 className="text-xl font-bold text-indigo-400">{profile.trustScore}</h4>
              </div>
              <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
                 <p className="text-slate-500 text-xs uppercase mb-1">{t.estPrice}</p>
                 <h4 className="text-xl font-bold text-white">${profile.priceRange.min} - ${profile.priceRange.max}</h4>
              </div>
            </div>
          </div>
        </div>

        {profile.sbtBadges && (
            <SbtBadgeList badges={profile.sbtBadges} language={language} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <AiAnalysisCard data={profile.aiData} clientScore={95} language={language} />

            <div>
              <div className="flex items-center justify-between mb-4">
                 <div>
                    <h3 className="text-xl font-bold text-white">{t.services}</h3>
                    <p className="text-sm text-slate-400">{t.servicesSubtitle}</p>
                 </div>
                 {isOwner && (
                     <button 
                        onClick={() => { setEditingService(null); setIsServiceModalOpen(true); }}
                        className="px-4 py-2 bg-indigo-600/20 text-indigo-300 hover:bg-indigo-600/30 rounded-lg text-sm font-medium transition-colors"
                     >
                        + {t.addService}
                     </button>
                 )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.packages.map(pkg => (
                  <ServiceCard 
                    key={pkg.id} 
                    pkg={pkg} 
                    language={language}
                    onBook={() => handleBookService(pkg, profile)}
                    isOwner={isOwner}
                    onEdit={() => { setEditingService(pkg); setIsServiceModalOpen(true); }}
                    onDelete={() => handleDeleteService(pkg)}
                  />
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white mb-4">{t.clientReviews}</h3>
              {profile.reviews.length > 0 ? (
                <div className="space-y-4">
                  {profile.reviews.map(review => (
                    <div key={review.id} className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                       <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                             <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold text-xs">
                                {review.reviewerName[0]}
                             </div>
                             <span className="font-bold text-white text-sm">{review.reviewerName}</span>
                          </div>
                          <div className="flex gap-1">
                             {[...Array(5)].map((_, i) => (
                                <span key={i} className={`text-sm ${i < review.rating ? 'text-yellow-400' : 'text-slate-600'}`}>★</span>
                             ))}
                          </div>
                       </div>
                       <p className="text-slate-300 text-sm">{review.comment}</p>
                       <p className="text-xs text-slate-500 mt-2">{review.date}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 italic">{t.noReviews}</p>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
               <h3 className="font-bold text-white mb-4">{t.performanceHistory}</h3>
               <div className="space-y-4">
                  {profile.recentCampaigns.map((camp, idx) => (
                     <div key={idx} className="flex justify-between items-center pb-3 border-b border-slate-700 last:border-0">
                        <span className="text-slate-300 text-sm">{camp.project}</span>
                        <span className="text-emerald-400 font-bold text-sm">{camp.roi}x ROI</span>
                     </div>
                  ))}
               </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProjectMarketplace = () => {
    if (grabModeEnabled) {
        return (
            <KolGrabMode 
                projects={projects} 
                language={language}
                onApply={handleApplyToProject}
                onBackToList={() => setGrabModeEnabled(false)}
            />
        );
    }

    const filteredProjects = projects.filter(p => 
        p.name.toLowerCase().includes(searchProjectQuery.toLowerCase()) || 
        p.tags.some(tag => tag.toLowerCase().includes(searchProjectQuery.toLowerCase()))
    );

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">{t.findProjectsTitle}</h1>
                    <p className="text-slate-400 text-sm">{t.findProjectsSubtitle}</p>
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <button 
                        onClick={() => setGrabModeEnabled(true)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 shadow-lg shadow-indigo-500/30"
                    >
                        <Layers size={16} /> {t.grabMode}
                    </button>
                    
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute left-3 top-2.5 text-slate-500 w-4 h-4" />
                        <input 
                            type="text" 
                            value={searchProjectQuery}
                            onChange={(e) => setSearchProjectQuery(e.target.value)}
                            placeholder={t.searchProjectsPlaceholder}
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                        />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map(proj => (
                    <div key={proj.id} className="bg-slate-800 border border-slate-700 rounded-xl p-6 hover:border-indigo-500/50 transition-all">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex gap-3">
                                <img src={proj.logo} alt={proj.name} className="w-12 h-12 rounded-lg object-cover" />
                                <div>
                                    <h3 className="font-bold text-white flex items-center gap-1">
                                        {proj.name}
                                        {proj.verified && <CheckCircle size={14} className="text-blue-500" />}
                                    </h3>
                                    <a href={proj.website} target="_blank" rel="noreferrer" className="text-xs text-indigo-400 hover:underline">
                                        {proj.website.replace('https://', '')}
                                    </a>
                                </div>
                            </div>
                            {proj.activeCampaigns > 0 && (
                                <span className="bg-emerald-500/10 text-emerald-400 text-xs px-2 py-1 rounded border border-emerald-500/20 flex items-center gap-1">
                                    <Clock size={10} /> {proj.activeCampaigns} Active
                                </span>
                            )}
                        </div>

                        <div className="mb-4">
                             <span className="bg-slate-700 text-slate-300 text-xs px-2 py-1 rounded mb-2 inline-block">
                                 {getCategoryLabel(proj.industry, language)}
                             </span>
                             <p className="text-sm text-slate-300 line-clamp-3">{proj.description}</p>
                        </div>

                        <div className="flex flex-wrap gap-1 mb-6">
                            {proj.tags.map(tag => (
                                <span key={tag} className="text-[10px] px-2 py-1 bg-slate-900 text-slate-400 rounded border border-slate-700">
                                    #{tag}
                                </span>
                            ))}
                        </div>

                        <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-700">
                            <div>
                                <p className="text-xs text-slate-500">{t.budgetRange}</p>
                                <p className="font-bold text-white">{proj.budgetRange}</p>
                            </div>
                            <button 
                                onClick={() => handleApplyToProject(proj)}
                                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                {t.applyNow}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
  };

  const renderMyCampaigns = () => {
    if (selectedCampaignForManagement) {
         // Detail View
         return (
             <div className="space-y-6 animate-in fade-in duration-500">
                 <button onClick={() => setSelectedCampaignForManagement(null)} className="flex items-center gap-2 text-slate-400 hover:text-white mb-2">
                     <ChevronLeft size={16} /> {t.backToDashboard}
                 </button>
                 
                 <div className="flex justify-between items-start">
                     <div>
                         <h1 className="text-2xl font-bold text-white">{selectedCampaignForManagement.name}</h1>
                         <p className="text-slate-400 text-sm">{t.campaignBudget}: {selectedCampaignForManagement.budgetRange}</p>
                     </div>
                     <span className={`px-3 py-1 rounded-full text-xs font-bold border ${selectedCampaignForManagement.escrowed ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                         {selectedCampaignForManagement.escrowed ? t.escrowed : t.notEscrowed}
                     </span>
                 </div>

                 <h3 className="text-lg font-bold text-white mt-8">{t.applicants}</h3>
                 <div className="space-y-4">
                     {selectedCampaignForManagement.applications && selectedCampaignForManagement.applications.length > 0 ? (
                         selectedCampaignForManagement.applications.map(app => (
                             <div key={app.id} className="bg-slate-800 border border-slate-700 rounded-xl p-5 flex flex-col md:flex-row gap-6">
                                 <div className="flex items-start gap-4 flex-1">
                                     <img src={app.kolAvatar} alt="" className="w-12 h-12 rounded-full object-cover" />
                                     <div>
                                         <h4 className="font-bold text-white flex items-center gap-2">
                                             {app.kolName}
                                             <span className="text-xs font-normal text-slate-400">{app.kolHandle}</span>
                                         </h4>
                                         <p className="text-sm text-slate-300 mt-2 bg-slate-900/50 p-3 rounded-lg border border-slate-700/50 italic">
                                             "{app.pitch}"
                                         </p>
                                     </div>
                                 </div>
                                 <div className="flex flex-col items-end gap-3 min-w-[150px]">
                                      <div className="text-right">
                                          <span className="text-xs text-slate-500 block">{t.proposedRate}</span>
                                          <span className="text-xl font-bold text-white">{app.proposedPrice} USDT</span>
                                      </div>
                                      <div className="flex gap-2 w-full">
                                          <button 
                                              onClick={() => handleChatWithUser(app.kolId, app.kolName, app.kolAvatar, UserRole.KOL)}
                                              className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded-lg transition-colors"
                                          >
                                              {t.sendMessage}
                                          </button>
                                          <button 
                                              onClick={() => handleAcceptApplicant(selectedCampaignForManagement.id, app)}
                                              className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg transition-colors font-bold"
                                          >
                                              {t.acceptOffer}
                                          </button>
                                      </div>
                                 </div>
                             </div>
                         ))
                     ) : (
                         <div className="text-center p-8 text-slate-500 italic border border-dashed border-slate-700 rounded-xl">
                             {t.noApplicants}
                         </div>
                     )}
                 </div>
             </div>
         );
    }

    const filteredMyCampaigns = projects.filter(p => 
        (p.activeCampaigns > 0 || (p.applications && p.applications.length > 0)) &&
        p.name.toLowerCase().includes(searchMyCampaignsQuery.toLowerCase())
    );

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
             <div className="flex justify-between items-center">
                 <h1 className="text-2xl font-bold text-white">{t.myCampaigns}</h1>
                 <button 
                    onClick={() => setCurrentView(ViewState.POST_CAMPAIGN)}
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                 >
                    <Plus size={18} /> {t.postCampaign}
                 </button>
             </div>

             <div className="relative">
                 <Search className="absolute left-3 top-2.5 text-slate-500 w-4 h-4" />
                 <input 
                     type="text" 
                     value={searchMyCampaignsQuery}
                     onChange={(e) => setSearchMyCampaignsQuery(e.target.value)}
                     placeholder={t.searchProjectsPlaceholder}
                     className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                 />
             </div>

             <div className="grid grid-cols-1 gap-4">
                 {filteredMyCampaigns.map(proj => (
                     <div key={proj.id} className="bg-slate-800 border border-slate-700 rounded-xl p-6 flex justify-between items-center hover:border-indigo-500/50 transition-all">
                         <div>
                             <h3 className="text-lg font-bold text-white">{proj.name}</h3>
                             <p className="text-sm text-slate-400">{t.budgetRange}: {proj.budgetRange}</p>
                             <div className="flex gap-2 mt-2">
                                 {proj.tags.map(tag => (
                                     <span key={tag} className="text-xs bg-slate-900 px-2 py-0.5 rounded text-slate-500">#{tag}</span>
                                 ))}
                             </div>
                         </div>
                         <div className="text-right">
                             <div className="mb-2">
                                 <span className="text-2xl font-bold text-white">{proj.applications?.length || 0}</span>
                                 <span className="text-xs text-slate-500 block">{t.applicants}</span>
                             </div>
                             <button 
                                onClick={() => setSelectedCampaignForManagement(proj)}
                                className="px-4 py-2 bg-indigo-600/20 text-indigo-300 hover:bg-indigo-600 hover:text-white rounded-lg text-sm font-medium transition-colors"
                             >
                                {t.viewApplicants}
                             </button>
                         </div>
                     </div>
                 ))}
                 {filteredMyCampaigns.length === 0 && (
                     <div className="text-center p-12 text-slate-500">
                         {t.noApplicants}
                     </div>
                 )}
             </div>
        </div>
    );
  };

  const renderOrders = () => {
    return (
       <div className="space-y-6 animate-in fade-in duration-500">
          <h1 className="text-2xl font-bold text-white">{t.contractManagement}</h1>
          
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
             <div className="overflow-x-auto">
                <table className="w-full text-left">
                   <thead className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wider">
                      <tr>
                         <th className="px-6 py-4">{userRole === UserRole.PROJECT ? t.kolColumn : t.projectColumn}</th>
                         <th className="px-6 py-4">{t.campaignColumn}</th>
                         <th className="px-6 py-4">{t.statusColumn}</th>
                         <th className="px-6 py-4">{t.amountColumn}</th>
                         <th className="px-6 py-4">{t.dueDateColumn}</th>
                         <th className="px-6 py-4">{t.actionsColumn}</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-700">
                      {orders.map(order => (
                         <tr key={order.id} className="hover:bg-slate-700/30 transition-colors">
                            <td className="px-6 py-4">
                               <div className="flex items-center gap-3">
                                  <img src={order.kolAvatar} alt="" className="w-8 h-8 rounded-full" />
                                  <span className="font-bold text-white">{userRole === UserRole.PROJECT ? order.kolName : order.projectName}</span>
                               </div>
                            </td>
                            <td className="px-6 py-4 text-slate-300 text-sm">{order.serviceTitle}</td>
                            <td className="px-6 py-4">
                               <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  order.status === OrderStatus.COMPLETED ? 'bg-emerald-500/10 text-emerald-400' :
                                  order.status === OrderStatus.IN_PROGRESS ? 'bg-indigo-500/10 text-indigo-400' :
                                  order.status === OrderStatus.SUBMITTED ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' :
                                  order.status === OrderStatus.PENDING_ACCEPTANCE ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                                  'bg-slate-700 text-slate-400'
                               }`}>
                                  {getStatusLabel(order.status, language)}
                               </span>
                            </td>
                            <td className="px-6 py-4 text-white font-mono">${order.amount}</td>
                            <td className="px-6 py-4 text-slate-400 text-sm">{order.dueDate}</td>
                            <td className="px-6 py-4 flex gap-2">
                               <button 
                                  onClick={() => { setSelectedOrder(order); setCurrentView(ViewState.ORDER_DETAIL); }}
                                  className="text-indigo-400 hover:text-white text-sm font-medium"
                               >
                                  {t.details}
                               </button>
                               {order.status === OrderStatus.COMPLETED && userRole === UserRole.PROJECT && !order.review && (
                                   <button 
                                      onClick={() => { setReviewingOrder(order); setIsReviewModalOpen(true); }}
                                      className="text-emerald-400 hover:text-white text-sm font-medium"
                                   >
                                      {t.leaveReview}
                                   </button>
                               )}
                               {order.status === OrderStatus.COMPLETED && order.review && (
                                   <span className="text-slate-500 text-xs flex items-center gap-1">
                                       <CheckCircle size={12} /> {t.reviewSubmitted}
                                   </span>
                               )}
                            </td>
                         </tr>
                      ))}
                   </tbody>
                </table>
             </div>
          </div>
       </div>
    );
  };

  // --- Conditional Render for Landing Page ---
  if (!hasEnteredApp) {
    return (
      <LandingView 
        onEnterApp={handleEnterApp} 
        language={language}
        onLanguageChange={setLanguage}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200 font-sans selection:bg-indigo-500/30">
      
      {/* Sidebar */}
      <Sidebar 
        currentView={currentView} 
        onChangeView={setCurrentView}
        language={language}
        userRole={userRole}
        onToggleRole={handleToggleRole}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Main Content Area */}
      <div className="md:ml-64 min-h-screen flex flex-col transition-all duration-300">
        
        {/* Header */}
        <header className="h-16 border-b border-slate-800 bg-slate-900/80 backdrop-blur-md sticky top-0 z-20 px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
             <button 
               onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
               className="md:hidden text-slate-400 hover:text-white"
             >
               <Menu size={24} />
             </button>
             <div className="hidden md:flex items-center gap-2 text-sm text-slate-500">
               <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
               {t.network}: BSC Mainnet
             </div>
          </div>

          <div className="flex items-center gap-4">
            <LanguageDropdown currentLanguage={language} onChange={setLanguage} />
            
            <div className="relative">
               <button 
                 onClick={() => setShowNotifications(!showNotifications)}
                 className="relative p-2 text-slate-400 hover:text-white transition-colors"
               >
                 <div className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full border-2 border-slate-900"></div>
                 <span className="sr-only">Notifications</span>
                 <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
               </button>
               {showNotifications && (
                 <NotificationDropdown 
                    notifications={notifications}
                    onMarkAsRead={(id) => setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))}
                    onMarkAllAsRead={() => setNotifications(prev => prev.map(n => ({ ...n, read: true })))}
                    onClose={() => setShowNotifications(false)}
                    language={language}
                 />
               )}
            </div>

            <button 
              onClick={() => setWalletConnected(!walletConnected)}
              className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                walletConnected 
                  ? 'bg-slate-800 text-emerald-400 border border-emerald-500/30' 
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-500/20'
              }`}
            >
              {walletConnected ? t.walletConnected : t.connectWallet}
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-x-hidden">
          {currentView === ViewState.DASHBOARD && renderDashboard()}
          {currentView === ViewState.ADMIN_DASHBOARD && <AdminDashboard language={language} orders={orders} usersCount={allKols.length + projects.length} />}
          {currentView === ViewState.MARKETPLACE && renderMarketplace()}
          {currentView === ViewState.PROJECT_MARKETPLACE && renderProjectMarketplace()}
          {currentView === ViewState.SQUADS && (
              <SquadMarketplace 
                  language={language} 
                  squads={squads} 
                  userRole={userRole}
                  myKolProfile={myKolProfile}
                  onCreateSquad={(newSquad) => setSquads([...squads, newSquad])}
                  allKols={allKols} 
                  onBookSquad={handleBookSquad}
              />
          )}
          {currentView === ViewState.MY_CAMPAIGNS && renderMyCampaigns()}
          {currentView === ViewState.POST_CAMPAIGN && <CreateCampaignView language={language} onPostCampaign={handlePostCampaign} />}
          {currentView === ViewState.KOL_PROFILE && renderKolProfile()}
          {currentView === ViewState.ORDERS && renderOrders()}
          {currentView === ViewState.ORDER_DETAIL && selectedOrder && (
             <OrderMonitoring 
                order={selectedOrder} 
                language={language} 
                onBack={() => setCurrentView(ViewState.ORDERS)}
                userRole={userRole}
                onSubmitWork={handleSubmitWork}
                onApproveOrder={handleApproveOrder}
                onRaiseDispute={handleRaiseDispute}
             />
          )}
          {currentView === ViewState.ANALYTICS && <AnalyticsDashboard language={language} userRole={userRole} onBack={() => setCurrentView(ViewState.DASHBOARD)} />}
          {currentView === ViewState.REFERRALS && <ReferralView language={language} userRole={userRole} />}
          {currentView === ViewState.DISPUTES && <DisputeResolutionView language={language} disputes={MOCK_DISPUTES} />}
          {currentView === ViewState.SETTINGS && (
              <SettingsView 
                  language={language} 
                  onLanguageChange={() => {}} 
                  userRole={userRole}
                  walletConnected={walletConnected}
                  onDisconnectWallet={() => setWalletConnected(false)}
                  onConnectWallet={() => setWalletConnected(true)}
              />
          )}
          {currentView === ViewState.CHAT && (
             <ChatView chats={chats} currentUserRole={userRole} language={language} />
          )}
        </main>
      </div>

      {/* Modals & Overlays */}
      <AiAssistant 
        isOpen={isAiChatOpen} 
        onClose={() => setIsAiChatOpen(false)} 
        kolName={aiChatContext}
        language={language}
        onFilterKols={handleAiFilter}
      />

      <ServiceEditModal 
        isOpen={isServiceModalOpen}
        onClose={() => setIsServiceModalOpen(false)}
        onSave={handleSaveService}
        service={editingService}
        language={language}
      />

      <ProfileEditModal 
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        onSave={handleSaveProfile}
        profile={myKolProfile}
        language={language}
      />

      <PlatformConnectModal
        isOpen={isPlatformModalOpen}
        onClose={() => setIsPlatformModalOpen(false)}
        onConnect={handleConnectPlatform}
        language={language}
      />
      
      {/* Service Booking Modal */}
      {bookingData && (
        <ServiceBookingModal 
           isOpen={isBookingModalOpen}
           onClose={() => setIsBookingModalOpen(false)}
           onConfirm={handleConfirmBooking}
           service={bookingData.pkg}
           kol={bookingData.kol}
           language={language}
        />
      )}

      {reviewingOrder && (
        <ReviewModal 
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          onSubmit={(rating, comment) => {
             const updatedOrders = orders.map(o => o.id === reviewingOrder.id ? { 
                 ...o, 
                 review: { 
                     id: `rev_${Date.now()}`, 
                     reviewerName: 'SuperNova Protocol', 
                     rating, 
                     comment, 
                     date: new Date().toISOString().split('T')[0],
                     orderId: o.id
                 } 
             } : o);
             setOrders(updatedOrders);
             setIsReviewModalOpen(false);
             setReviewingOrder(null);
          }}
          kolName={reviewingOrder.kolName}
          language={language}
        />
      )}

      {applyingProject && (
        <ProjectApplicationModal 
          isOpen={isApplicationModalOpen}
          onClose={() => setIsApplicationModalOpen(false)}
          onSubmit={submitApplication}
          project={applyingProject}
          language={language}
        />
      )}

    </div>
  );
};

export default App;